import { Component, Input, Output, EventEmitter, ViewChild, OnChanges, SimpleChanges, AfterViewChecked } from '@angular/core';
import { forkJoin } from 'rxjs';
import { MessageService, ConfirmationService } from 'primeng/api';
import { LiveShopService } from 'src/app/services/liveshop.service';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { AccountSelectorComponent } from 'src/app/components/account-selector/account-selector.component';
import { TabView } from 'primeng/tabview';

@Component({
  selector: 'app-usage-rights',
  templateUrl: './usage-rights.component.html',
  styleUrls: ['./usage-rights.component.scss']
})
export class UsageRightsComponent implements OnChanges, AfterViewChecked {

  @ViewChild(AccountSelectorComponent) accountSelector!: AccountSelectorComponent;
  @ViewChild(TabView) tabViewRef?: TabView;

  // Chamado pelo pai (via ViewChild) quando o p-dialog que envolve este
  // componente termina de abrir — o TabView calcula a posição do "ink bar"
  // no primeiro ciclo de render, que acontece com o dialog ainda invisível
  // (largura 0), então precisa recalcular manualmente depois que abre de verdade.
  recalcularTabs(): void {
    setTimeout(() => this.tabViewRef?.updateInkBar(), 0);
  }

  @Input() items: any[] = [];
  @Input() mode: 'dialog' | 'panel' = 'dialog';

  @Output() saved = new EventEmitter<{ items: any[], keyword: string }>();
  @Output() commentSent = new EventEmitter<{ items: any[] }>();

  mentionedIgAccounts: string[] = [];
  direitosCollapsed: { auto: boolean; manual: boolean } = { auto: true, manual: false };
  

  selectedInstagramAccountId: string = '';
  selectedFbid: string = '';
  selectedInstagramHandle: string = '';
  selectedInstagramAvatar: string = '';
  instagramAccountSelecionadaValida = false;

  mensagemDireitos: string = '';
  urKeyWord: string = '';
  private lastUrKeyWord: string = '';
  private lastValidMessage: string = '';
  private handleParaAutoSelect: string = '';

  displayAguardeComment = false;
  sendDireitos = false;
  private needsAccountLoad = false;

  constructor(
    private liveShopService: LiveShopService,
    private messageService: MessageService,
    private errorHandler: ErrorHandlerService,
    private confirmationService: ConfirmationService
  ) {}

  // Tab "Comentário enviado" — editar texto não é suportado pela Graph API do Instagram,
  // então só oferecemos excluir (ver InstagramService::deleteComment).
  excluindoComentario = false;

  get sentCommentId(): string | null {
    return this.items?.[0]?.ur_comment_id ?? null;
  }

  get sentCommentValue(): string | null {
    return this.items?.[0]?.ur_comment_value ?? null;
  }

  // Id da resposta que efetivamente aprovou (ou rejeitou) a solicitação — usado só pra
  // marcar qual das respostas ao vivo ganha o check verde, ver processMetaMention/HK067.
  get approvedReplyId(): string | null {
    return this.items?.[0]?.ur_reply_id ?? null;
  }

  get replyApproved(): boolean {
    return this.items?.[0]?.usage_rights === 'approved';
  }

  // Quem responde é normalmente o dono do post — avatar/nome já foram capturados em
  // insights.insights na importação/webhook. Usado como fallback quando a resposta ao vivo
  // não traz foto (a Graph API não retorna avatar em /replies).
  get replyAuthorAvatar(): string | null {
    return this.items?.[0]?.insights?.insights?.profile_picture ?? null;
  }

  get replyAuthorName(): string | null {
    const insights = this.items?.[0]?.insights?.insights;
    return insights?.name || insights?.username || null;
  }

  replyAuthorAvatarFor(username?: string): string | null {
    const insights = this.items?.[0]?.insights?.insights;
    if (username && insights?.username && username.toLowerCase() === insights.username.toLowerCase()) {
      return insights.profile_picture ?? null;
    }
    return null;
  }

  // Histórico de envios (auto ou manual) — mais recente primeiro. Cada item guarda a
  // conta e o texto usados naquele envio específico, já que ambos podem mudar entre envios.
  get sentComments(): { comment_id: string; sent_at: string; handle?: string; avatar?: string; message?: string; fbid?: string }[] {
    return this.items?.[0]?.ur_sent_comments ?? [];
  }

  showReplyFor: string | null = null;

  toggleReply(commentId: string): void {
    this.showReplyFor = this.showReplyFor === commentId ? null : commentId;
  }

  // Depois do 1º envio, só a palavra-chave fica travada — reflete a trava aplicada no
  // backend. Texto e conta do Instagram podem ser alterados livremente a qualquer momento.
  get isLocked(): boolean {
    return this.sentComments.length > 0;
  }

  // Instagram só permite excluir comentário em mídia da própria conta — em mídia de
  // terceiro (mencionada), a API aceita criar/ler mas recusa delete. Comparamos o dono
  // real do post (salvo em insights.insights.username na importação/webhook) com a conta
  // que efetivamente enviou aquele comentário específico do histórico.
  isOwnMediaFor(handle?: string): boolean {
    const mediaOwner = (this.items?.[0]?.insights?.insights?.username ?? '').toLowerCase();
    return !!mediaOwner && !!handle && mediaOwner === handle.toLowerCase();
  }

  get showComentarioEnviadoTab(): boolean {
    return this.sentComments.length > 0;
  }

  excluirComentarioEnviado(commentId: string): void {
    if (!commentId) return;

    // O comentário pode ter sido postado por uma conta diferente da que está selecionada
    // no dropdown agora (conta não fica mais travada) — usamos o fbid salvo naquele envio.
    const envio = this.sentComments.find(c => c.comment_id === commentId);
    const fbid = envio?.fbid || this.selectedFbid;

    this.confirmationService.confirm({
      message: 'Deseja realmente excluir este comentário do Instagram?',
      header: 'Excluir comentário',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Excluir',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        const item0 = this.items[0];
        this.excluindoComentario = true;

        this.liveShopService.excluirComentario(commentId, item0.content.uid, fbid).subscribe({
          next: (res: any) => {
            this.excluindoComentario = false;
            const remaining = res?.sent_comments ?? [];
            for (const item of this.items) {
              item.ur_sent_comments = remaining;
              item.ur_comment_id = remaining[0]?.comment_id ?? null;
              if (!remaining.length) {
                item.ur_comment_value = null;
                item.usage_rights = 'pending';
              }
            }
            this.messageService.add({
              severity: 'success',
              summary: 'Comentário excluído',
              detail: 'O comentário foi removido do Instagram com sucesso.'
            });
          },
          error: (err) => {
            this.excluindoComentario = false;
            this.errorHandler.handle(err, 'Falha ao excluir comentário.');
          }
        });
      }
    });
  }
  activeTabIndex: number = 0;
  ngOnChanges(changes: SimpleChanges): void {
    this.activeTabIndex = 0;
    if (changes['items'] && this.items?.length) {
      this.initialize();
    }
  }

  ngAfterViewChecked(): void {
    if (this.needsAccountLoad && this.accountSelector) {
      this.needsAccountLoad = false;
      this.accountSelector.loadAccounts();
    }
  }

  initialize(): void {
    const primeiro = this.items[0];
    if (!primeiro) return;

    this.selectedInstagramHandle = '';
    this.selectedInstagramAccountId = '';
    this.selectedInstagramAvatar = '';
    this.instagramAccountSelecionadaValida = false;
    this.mentionedIgAccounts = primeiro?.mentioned_ig_accounts ?? [];
    this.direitosCollapsed = { auto: true, manual: false };
    this.displayAguardeComment = false;
    this.sendDireitos = false;

    const commentValue = primeiro?.ur_comment_value ?? '';
    const savedKeyword = primeiro?.ur_key_word ? primeiro.ur_key_word.replace(/^#/, '') : '';
    this.urKeyWord = savedKeyword;
    this.lastUrKeyWord = savedKeyword;

    if (commentValue) {
      this.mensagemDireitos = commentValue;
      this.lastValidMessage = commentValue;
      const handleMatch = commentValue.match(/@(\w+)/);
      this.handleParaAutoSelect = handleMatch ? handleMatch[1] : '';
    } else {
      this.mensagemDireitos = this.templateDireitos;
      this.lastValidMessage = this.mensagemDireitos;
      this.handleParaAutoSelect = '';
    }

    if (this.accountSelector) {
      this.accountSelector.loadAccounts();
    } else {
      this.needsAccountLoad = true;
    }

    this.liveTextByCommentId = {};
    this.repliesByCommentId = {};
    this.loadLiveCommentTexts();
  }

  // Texto exibido na aba "Comentários enviados" vindo direto da Graph API (GET /{comment-id}),
  // não do que salvamos no banco — o dono do post pode editar o comentário manualmente pelo
  // app do Instagram, e a API não nos avisa disso, então o texto salvo pode ficar desatualizado.
  liveTextByCommentId: Record<string, string | null> = {};

  // Respostas ao vivo de cada comentário (GET /{comment-id}/replies) — não confiamos no
  // ur_reply_value salvo no banco porque ele guarda só a última resposta que bateu a keyword
  // de aprovação, e um bug anterior podia sobrescrevê-lo com o próprio comentário do app
  // (ver processMetaMention/HK067). Aqui mostramos todas as respostas reais, sempre atuais.
  repliesByCommentId: Record<string, { id: string; text: string; timestamp: string; from?: { id: string; username: string } }[]> = {};

  private loadLiveCommentTexts(): void {
    const item0 = this.items[0];
    if (!item0 || !this.sentComments.length) return;

    type LiveComment = { comment_id: string; text: string | null; replies: { id: string; text: string; timestamp: string; from?: { id: string; username: string } }[] };

    this.liveShopService.getSentCommentsLive(item0.content.uid).subscribe({
      next: (res: { comments: LiveComment[] }) => {
        const textMap: Record<string, string | null> = {};
        const repliesMap: Record<string, { id: string; text: string; timestamp: string; from?: { id: string; username: string } }[]> = {};
        for (const c of res.comments ?? []) {
          textMap[c.comment_id] = c.text;
          repliesMap[c.comment_id] = c.replies ?? [];
        }
        this.liveTextByCommentId = textMap;
        this.repliesByCommentId = repliesMap;
      },
      error: () => {} // silencioso — mantém o texto salvo como fallback
    });
  }

  get contaMencionada(): boolean {
    return this.instagramAccountSelecionadaValida &&
      !!this.selectedInstagramHandle &&
      this.mentionedIgAccounts.map(u => u.toLowerCase()).includes(this.selectedInstagramHandle.toLowerCase());
  }

  get urKeyWordValida(): boolean {
    return this.urKeyWord.trim().length >= 3;
  }

  private keywordRegex(): RegExp | null {
    if (!this.urKeyWordValida) return null;
    const kw = this.urKeyWord.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp('#' + kw + '(?!\\w)');
  }

  get mensagemValida(): boolean {
    if (!this.instagramAccountSelecionadaValida || !this.selectedInstagramHandle) return false;
    const msg = this.mensagemDireitos.trim();
    const hasHandle = msg.includes('@' + this.selectedInstagramHandle);
    const regex = this.keywordRegex();
    const hasKeyword = regex ? regex.test(msg) : /#\w{3,}(?!\w)/.test(msg);
    return hasHandle && hasKeyword;
  }

  get alreadySentDireitos(): boolean {
    return this.items.some(
      i => i?.usage_rights && i.usage_rights !== 'pending'
    );
  }

  private get templateDireitos(): string {
    const handle = this.selectedInstagramHandle ? '@' + this.selectedInstagramHandle : '@handle';
    const kw = '#' + (this.urKeyWordValida ? this.urKeyWord.trim() : 'palavrachave');
    return `Olá! Gostaríamos de usar este conteúdo em nossos materiais. Para autorizar, responda a este comentário mencionando ${handle} e incluindo a palavra-chave ${kw}`;
  }

  toggleDireitos(key: 'auto' | 'manual'): void {
    if (key === 'auto' && !this.contaMencionada) return;
    this.direitosCollapsed[key] = !this.direitosCollapsed[key];
  }

  onKeywordChange(): void {
    const newKeyword = this.urKeyWord.trim();
    const oldKeyword = this.lastUrKeyWord.trim();
    const replacement = newKeyword.length >= 3 ? '#' + newKeyword : '#palavrachave';

    if (this.mensagemDireitos.trim()) {
      if (oldKeyword.length >= 3) {
        this.mensagemDireitos = this.mensagemDireitos.replace('#' + oldKeyword, replacement);
      } else {
        this.mensagemDireitos = this.mensagemDireitos.replace('#palavrachave', replacement);
      }
    }

    this.lastUrKeyWord = this.urKeyWord;
    this.lastValidMessage = this.mensagemDireitos;
  }

  onInstagramAccountSelecionada(event: { instagramId: string, fbid: string, account?: any }): void {
    const oldHandle = this.selectedInstagramHandle;
    this.selectedInstagramAccountId = event.instagramId;
    this.selectedFbid = event.fbid;
    this.selectedInstagramHandle = event.account?.label || '';
    this.selectedInstagramAvatar = event.account?.picture || '';
    this.instagramAccountSelecionadaValida = !!event.instagramId && event.instagramId !== 'add_account';

    if (!this.mensagemDireitos.trim()) {
      this.mensagemDireitos = this.templateDireitos;
    } else if (this.selectedInstagramHandle) {
      const oldHandleStr = '@' + (oldHandle || 'handle');
      this.mensagemDireitos = this.mensagemDireitos.replace(oldHandleStr, '@' + this.selectedInstagramHandle);
    }
    this.lastValidMessage = this.mensagemDireitos;

    if (this.mentionedIgAccounts.map(u => u.toLowerCase()).includes((this.selectedInstagramHandle || '').toLowerCase())) {
      this.direitosCollapsed = { auto: false, manual: true };
    } else {
      this.direitosCollapsed = { auto: true, manual: false };
    }
  }

  onAccountsCarregadas(contas: any[]): void {
    if (!this.handleParaAutoSelect) return;
    const match = contas.find(c => c.label === this.handleParaAutoSelect && c.value !== 'add_account');
    if (match) {
      this.selectedInstagramAccountId = match.value;
      this.selectedFbid = match.fbid || '';
      this.selectedInstagramHandle = match.label || '';
      this.selectedInstagramAvatar = match.picture || '';
      this.instagramAccountSelecionadaValida = true;
      if (this.mentionedIgAccounts.map(u => u.toLowerCase()).includes((match.label || '').toLowerCase())) {
        this.direitosCollapsed = { auto: false, manual: true };
      }
    }
    this.handleParaAutoSelect = '';
  }

  private protectedHandle(): string {
    return '@' + (this.selectedInstagramHandle || 'handle');
  }

  private protectedKeyword(): string {
    return '#' + (this.urKeyWordValida ? this.urKeyWord.trim() : 'palavrachave');
  }

  private textHasProtectedTokens(text: string): boolean {
    const handle = this.protectedHandle();
    const kw = this.protectedKeyword();
    const kwRegex = new RegExp(kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '(?!\\w)');
    return text.includes(handle) && kwRegex.test(text);
  }

  onTextareaKeydown(event: KeyboardEvent, el: HTMLTextAreaElement): void {
    const keyword = this.protectedKeyword();
    const text = el.value;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;

    if (event.key === 'Backspace' || event.key === 'Delete') {
      let resultText: string;
      if (start !== end) {
        resultText = text.slice(0, start) + text.slice(end);
      } else if (event.key === 'Backspace') {
        resultText = text.slice(0, Math.max(0, start - 1)) + text.slice(start);
      } else {
        resultText = text.slice(0, start) + text.slice(start + 1);
      }
      if (!this.textHasProtectedTokens(resultText)) {
        event.preventDefault();
      }
      return;
    }

    if (event.key.length === 1 && /\w/.test(event.key) && !event.ctrlKey && !event.metaKey) {
      const kwEnd = text.indexOf(keyword) + keyword.length;
      if (start === end && start === kwEnd) {
        event.preventDefault();
      }
    }
  }

  onTextareaInput(el: HTMLTextAreaElement): void {
    if (!this.textHasProtectedTokens(this.mensagemDireitos)) {
      this.mensagemDireitos = this.lastValidMessage;
      setTimeout(() => { el.value = this.lastValidMessage; }, 0);
    } else {
      this.lastValidMessage = this.mensagemDireitos;
    }
  }

  private extractKeywordFromMessage(msg: string): string {
    const match = msg.match(/#(\w{3,})/);
    return match ? '#' + match[1] : '';
  }

  salvarEEnviarDireitos(): void {
    const keyword = this.extractKeywordFromMessage(this.mensagemDireitos);
    this.displayAguardeComment = true;

    const saveRequests = this.items.map(item =>
      this.liveShopService.saveUrKeyWord(item.content.uid, keyword, this.mensagemDireitos)
    );

    forkJoin(saveRequests).subscribe({
      next: () => this.enviarComentarioDireitos(),
      error: (err) => {
        this.displayAguardeComment = false;
        this.errorHandler.handle(err, 'Falha ao salvar palavra-chave de direitos.');
      }
    });
  }

  private enviarComentarioDireitos(): void {
    const mensagem = this.mensagemDireitos;
    this.displayAguardeComment = true;
    this.sendDireitos = true;

    const requests = this.items.map(item => {
      const mediaIdParaComentario = (item.parent_id && item.parent_id !== item.media_id)
        ? item.parent_id
        : item.media_id;
      return this.liveShopService.postarComentario(
        mediaIdParaComentario,
        mensagem,
        item.feed_uid,
        item.content.uid,
        this.selectedFbid,
        this.selectedInstagramAccountId,
        item.media_creator?.handle,
        this.selectedInstagramHandle,
        this.selectedInstagramAvatar
      );
    });

    forkJoin(requests).subscribe({
      next: (results: any[]) => {
        this.items.forEach((item, i) => {
          const commentId = results[i]?.comment_id ?? null;
          item.usage_rights = 'waiting';
          item.ur_comment_value = this.mensagemDireitos;
          item.ur_comment_id = commentId;
          item.ur_sent_comments = [
            {
              comment_id: commentId,
              sent_at: new Date().toISOString(),
              handle: this.selectedInstagramHandle,
              avatar: this.selectedInstagramAvatar,
              message: this.mensagemDireitos
            },
            ...(item.ur_sent_comments ?? [])
          ];
        });

        this.messageService.add({
          severity: 'success',
          summary: 'Solicitação enviada',
          detail: 'Os comentários foram postados com sucesso!'
        });
        this.sendDireitos = false;
        this.displayAguardeComment = false;
        this.commentSent.emit({ items: this.items });
      },
      error: (err) => {
        this.displayAguardeComment = false;
        this.sendDireitos = false;
        this.errorHandler.handle(err, 'Falha ao enviar comentário de direitos.');
      }
    });
  }

  salvarKeyWordDireitos(): void {
    const keyword = this.extractKeywordFromMessage(this.mensagemDireitos);
    const item0 = this.items[0];
    const permalink = item0?.permalink ?? item0?.media_creator?.permalink ?? null;

    navigator.clipboard.writeText(this.mensagemDireitos).catch(() => {});

    this.displayAguardeComment = true;

    const requests = this.items.map(item =>
      this.liveShopService.saveUrKeyWord(item.content.uid, keyword, this.mensagemDireitos)
    );

    forkJoin(requests).subscribe({
      next: () => {
        for (const item of this.items) {
          item.usage_rights = 'waiting';
        }

        this.messageService.add({
          severity: 'success',
          summary: 'Salvo e copiado',
          detail: 'Comentário copiado para a área de transferência e palavra-chave salva!'
        });
        this.displayAguardeComment = false;

        if (permalink) {
          window.open(permalink, '_blank');
        }

        this.saved.emit({ items: this.items, keyword });
      },
      error: (err) => {
        this.displayAguardeComment = false;
        this.errorHandler.handle(err, 'Falha ao salvar palavra-chave de direitos.');
      }
    });
  }
}
